import { useCallback, useLayoutEffect, useMemo, useState } from 'react'

import { type ElementSize, useResizeElement } from '@/lib/hooks/use-resize-element'
import { cn } from '@/lib/utils'

import { type ChartContextType, LineChartContext } from './line-chart-context'
import { LineChartHoverZone } from './line-chart-hover-zone'
import { scaleX, scaleY } from './utils'
import { getNiceDomain } from './utils'

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: Record<string, any>[]
  dataKeys: string[]
  /** If not specified, it becomes responsive to the width of container element */
  plotAreaWidth?: number
  minPlotAreaWidth?: number
  plotAreaHeight: number
  plotAreaPadding?: {
    top?: number
    right?: number
    bottom?: number
    left?: number
  }
  yAxisWidth?: number
  xAxisHeight?: number
  children: React.ReactNode
}

export const LineChart: React.FC<Props> = ({
  data,
  dataKeys: dataKey,
  plotAreaWidth,
  minPlotAreaWidth,
  plotAreaHeight,
  plotAreaPadding = { top: 0, right: 0, bottom: 0, left: 0 },
  yAxisWidth = 80,
  xAxisHeight = 40,
  children,
}) => {
  const [dimension, setDimension] = useState<ElementSize>({
    width: 0,
    height: 0,
  })
  const { elementRef, size } = useResizeElement()

  const dataSets: Record<string, (number | null)[]> = useMemo(() => {
    return dataKey.reduce((accum, k) => {
      return {
        ...accum,
        [k]: data.map((entry) => {
          return entry[k] as number | null
        }),
      }
    }, {})
  }, [data, dataKey])

  const validDataSets: Record<string, number[]> = useMemo(() => {
    return Object.keys(dataSets).reduce((accum, k) => {
      return {
        ...accum,
        [k]: dataSets[k].filter((value) => value !== null),
      }
    }, {})
  }, [dataSets])

  const { niceMin, niceMax, ticks } = useMemo(() => {
    const wholeDataSet = Object.values(validDataSets).flat()

    if (wholeDataSet.length === 0) {
      return { niceMin: 0, niceMax: 0, ticks: [] }
    }

    const min = Math.min(...wholeDataSet)
    const max = Math.max(...wholeDataSet)

    return getNiceDomain(min, max)
  }, [validDataSets])

  const xScale = useCallback(
    (index: number) => {
      return scaleX({
        index,
        width: plotAreaWidth ?? dimension.width - yAxisWidth,
        paddingLeft: plotAreaPadding?.left,
        paddingRight: plotAreaPadding?.right,
        numPoints: data.length,
      })
    },
    [plotAreaWidth, plotAreaPadding, data, dimension.width, yAxisWidth]
  )

  const yScale = useCallback(
    (value: number) => {
      return scaleY({
        value,
        height: plotAreaHeight,
        paddingTop: plotAreaPadding?.top,
        min: niceMin,
        max: niceMax,
      })
    },
    [plotAreaHeight, plotAreaPadding, niceMin, niceMax]
  )

  const [activeIndex, setActivePointIndex] = useState<number | null>(null)

  const handleActiveIndexChange = useCallback(
    (index: number | null) => {
      if (index === null) {
        setActivePointIndex(null)
        return
      }

      const values = Object.values(dataSets).reduce((accum, dataSet) => {
        const value = dataSet[index]

        if (value === null) {
          return accum
        }

        return [...accum, value]
      }, [])

      if (values.length === 0) {
        return
      }

      setActivePointIndex(index)
    },
    [dataSets]
  )

  const contextValues: ChartContextType = useMemo(() => {
    return {
      data,
      dataSets,
      validDataSets,
      activeIndex,
      plotAreaWidth: plotAreaWidth ?? dimension.width - yAxisWidth,
      plotAreaHeight,
      plotAreaPadding,
      xAxisHeight,
      yAxisWidth,
      ticks,
      niceMin,
      niceMax,
      xScale,
      yScale,
    }
  }, [
    data,
    dataSets,
    validDataSets,
    activeIndex,
    plotAreaWidth,
    plotAreaHeight,
    plotAreaPadding,
    xAxisHeight,
    yAxisWidth,
    ticks,
    niceMin,
    niceMax,
    xScale,
    yScale,
    dimension.width,
  ])

  useLayoutEffect(() => {
    if (!plotAreaWidth) {
      const { width, height } = size
      const widthClamped = minPlotAreaWidth ? Math.max(width, minPlotAreaWidth) : width
      setDimension({
        width: widthClamped,
        height,
      })
    }
  }, [plotAreaWidth, minPlotAreaWidth, size])

  return (
    <LineChartContext.Provider value={contextValues}>
      <div ref={elementRef} className={cn('relative', 'w-full', 'h-full')}>
        <svg
          overflow="visible"
          width={plotAreaWidth ? plotAreaWidth + yAxisWidth : dimension.width}
          height={plotAreaHeight + xAxisHeight + (plotAreaPadding?.top ?? 0)}
        >
          {children}
        </svg>

        <div
          style={{
            position: 'absolute',
            overflow: 'visible',
            top: 0,
            left: 0,
            width: plotAreaWidth ?? dimension.width,
            height: plotAreaHeight,
          }}
        >
          <LineChartHoverZone onActiveIndexChange={handleActiveIndexChange} />
        </div>
      </div>
    </LineChartContext.Provider>
  )
}
